import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { STORE_PHONE } from "@/lib/brand";
import { Sparkles, Phone, ArrowRight } from "lucide-react";

/**
 * NotFound — branded 404 page. Suggests the most likely next steps a lost
 * visitor actually wants (shop prom, browse schools, ai try-on, call Heather).
 */
export default function NotFound() {
  const location = useLocation();

  const suggestions = [
    { to: "/prom-dresses", label: "Prom dresses" },
    { to: "/homecoming-dresses", label: "Homecoming" },
    { to: "/graduation-dresses", label: "Graduation" },
    { to: "/tuxedos", label: "Men's tuxedos" },
    { to: "/schools", label: "School directory" },
    { to: "/try-on", label: "AI Try-On" },
  ];

  return (
    <div className="min-h-[80vh] bg-ivory flex items-center">
      <Seo title="Page not found" description="The page you're looking for isn't here." path={location.pathname} noindex />

      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <motion.p
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-4"
        >
          <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5" /> ERROR 404
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-display text-6xl md:text-8xl leading-[0.9] text-ink"
        >
          This dress<br /><em className="italic text-rosemetal">isn't in our rack.</em>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="font-body text-base md:text-lg text-ink/70 mt-6 max-w-lg mx-auto leading-relaxed"
        >
          The page you were looking for isn't here — either the link is old or the dress moved silos.
          Try one of these instead:
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-10 flex flex-wrap justify-center gap-2"
          data-testid="notfound-suggestions"
        >
          {suggestions.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="border border-cream rounded-full px-5 py-2.5 font-body text-sm text-ink hover:border-rosemetal hover:text-rosemetal transition-colors"
            >
              {s.label}
            </Link>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55 }}
          className="mt-12 pt-8 border-t border-cream flex flex-wrap justify-center gap-4 items-center"
        >
          <Link
            to="/"
            data-testid="notfound-home"
            className="bg-ink text-cream px-6 py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-rosemetal transition-colors flex items-center gap-2"
          >
            BACK TO HOMEPAGE <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <a
            href={`tel:${STORE_PHONE.replace(/\D/g, "")}`}
            className="border border-ink text-ink px-6 py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-ink hover:text-cream transition-colors flex items-center gap-2"
          >
            <Phone className="w-3.5 h-3.5" /> CALL HEATHER · {STORE_PHONE}
          </a>
        </motion.div>
      </div>
    </div>
  );
}
